import React, { useCallback, useEffect, useState } from 'react';

// Hooks and services
import { useAppSelector } from '../../../redux/hook';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { getGeofenceChangelog, updateGeofences } from '../../../services/geofence.service';
import AuthService from '../../../services/auth.service';

// Utils
import { calculateCentroid } from '../../../utils/mapHelper';

// Constants
import { CONFIRM_DIALOG_CANCEL } from '../../../constants/Constants';
import { AppPermissions } from '../../../constants/Permissions';

// Types
import { PaginatedRequest } from '../../../requestModels/paginatedRequest';
import { GeoPosition } from '../../../types/GeoPosition';
import { LatLng } from 'leaflet';
import { Geofence } from '../../../types/Geofence';
import { Coordinates } from '../../../types/Coordinates';

// MUI
import { Grid, Tab } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';

// Components
import Map from '../../maps/Map';
import AirportGeofences from './airportGeofences';
import FacilityGeofences from './facilityGeofences';
import ChangeLogList from '../../common/ChangeLog';

// Types
import { PaginatedResult } from '../../../types/PaginatedResult';
import { ChangeLog } from '../../../types/ChangeLog';
import { SingleResult } from '../../../types/SingleResult';

// Styles
import '../../../styles/muiTabs.scss';
import './Geofences.scss';


export default function Geofences(): JSX.Element {
  const { notifyMessage } = useNotifyMessage();
  const geofenceState = useAppSelector((state) => state.geofences);
  const [activeLeftTab, setActiveLeftTab] = useState('map');
  const [activeRightTab, setActiveTab] = useState('airports');
  const [centerMap, setCenterMap] = useState<LatLng>();
  const [positions, setPositions] = useState<Coordinates[]>([]);
  const [selectedGeofence, setSelectedGeofence] = useState<Geofence | null>();
  const { renderDialog, confirm } = useConfirmDialog(CONFIRM_DIALOG_CANCEL);

  const handleRightTabchange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedGeofence(null);
    setActiveTab(newValue);
  };

  const handleGeofenceSelected = (item: Geofence) => {
    setSelectedGeofence(item);
    createMap(item);
  };

  useEffect(() => {
    if (geofenceState.stationGeofences && geofenceState.stationGeofences.length > 0 && activeRightTab === 'airports') {
      const geofence = selectedGeofence ? selectedGeofence : geofenceState.stationGeofences[0];
      createMap(geofence);
    }

    if (geofenceState.facilityGeofences && geofenceState.facilityGeofences.length > 0 && activeRightTab === 'facility') {
      const geofence = selectedGeofence ? selectedGeofence : geofenceState.facilityGeofences[0];
      createMap(geofence);
    }
  }, [geofenceState]);

  const createMap = (item: Geofence) => {
    if (!item) return;

    const centroidMap = calculateCentroid(item?.coordinates ?? []);
    if (centroidMap) {
      setCenterMap(centroidMap);
    } else if (item.location) {
      setCenterMap(new LatLng(item.location.latitude, item.location.longitude));
    }

    setPositions([
      {
        latLng: {
          lat: item.location?.latitude,
          lng: item.location?.longitude,
        },
        color: 'green',
        fillColor: 'green',
        markerName: item.name,
        showMarker: true,
        showCircleMarker: true,
        fillOpacity: 0.1,
        circleRadius: 300,
      } as Coordinates,
    ]);

    if (item.coordinates && item.coordinates.length > 0) {
      setPositions((prevState) => [
        ...prevState,
        {
          geofences: [
            {
              name: '',
              coordinates: item.coordinates,
            },
          ],
          editable: true,
          showPolygon: true,
        } as Coordinates,
      ]);
    }
  };

  const updateGeofence = async (payload: Geofence) => {
    await updateGeofences(payload)
      .then(() => {
        notifyMessage('Update Geofence success!', 'success');
        handleGeofenceSelected(payload);
      })
      .catch(() => {
        notifyMessage('Unable to Geofence', 'error');
      });
  };

  const handleCreatePolygon = async (e) => {
    const { layerType, layer } = e;
    if (layerType === 'polygon') {
      const latlngs = layer.getLatLngs()[0];
      if (selectedGeofence) {
        const coordinates = latlngs.map((f: LatLng) => [f.lng, f.lat]);
        const centroid = calculateCentroid(coordinates);
        updateGeofence({
          ...selectedGeofence,
          coordinates: latlngs.map((f: LatLng) => [f.lng, f.lat]),
          location: {
            latitude: centroid?.lat,
            longitude: centroid?.lng,
            type: 'Point',
          } as GeoPosition,
        });
      }
    }
  };

  const handleEditPolygon = (e) => {
    const {
      layers: { _layers },
    } = e;

    Object.values(_layers).map(({ editing }: any) => {
      if (selectedGeofence && editing.latlngs.length > 0) {
        const latlngs = editing.latlngs[0][0];
        const coordinates = latlngs.map((f) => [f.lng, f.lat]);
        const centroid = calculateCentroid(coordinates);
        updateGeofence({
          ...selectedGeofence,
          coordinates: coordinates,
          location: {
            latitude: centroid?.lat,
            longitude: centroid?.lng,
            type: 'Point',
          } as GeoPosition,
        });
      }
    });
  };

  const handleDeletePolygon = useCallback(
    async (_e) => {
      const { accepted } = await confirm({});
      if (!accepted) return;
      if (selectedGeofence) {
        updateGeofence({
          ...selectedGeofence,
          coordinates: [],
        });
      }
    },
    [confirm],
  );

  const getChangeLog = async (request: PaginatedRequest): Promise<SingleResult<PaginatedResult<ChangeLog>> | null> => {
    if (selectedGeofence) return await getGeofenceChangelog(selectedGeofence.id, request);
    return null;
  };

  return (
    <React.Fragment>
      {renderDialog()}
      <div className='geofences'>
        <div className='geofences__container'>
          <Grid container spacing={0.5}>
            <Grid item xs={12} sm={7} lg={8}>
              <div className='geofences__card'>
                <TabContext value={activeLeftTab}>
                  <div className='app-tabs'>
                    <TabList onChange={(_e, value) => setActiveLeftTab(value)}>
                      <Tab label='Map' value='map' sx={{ textTransform: 'none' }} />
                      <Tab label='Changelog' value='changelog' sx={{ textTransform: 'none' }} />
                    </TabList>
                  </div>
                  <TabPanel value='map' className='geofences__panel'>
                    {centerMap && geofenceState.stationGeofences && (
                      <Map
                        onCreated={handleCreatePolygon}
                        onEdited={handleEditPolygon}
                        onDeleted={handleDeletePolygon}
                        center={centerMap}
                        coordinates={positions}
                        showToggleButtons={false}
                        isEditingEnabled={selectedGeofence !== null && AuthService.hasPermission(AppPermissions.Geofence.Edit)}
                        height={'80vh'}
                      />
                    )}
                  </TabPanel>
                  <TabPanel value='changelog' className='geofences__panel'>
                    <ChangeLogList fetchAsync={getChangeLog} />
                  </TabPanel>
                </TabContext>
              </div>
            </Grid>

            <Grid item xs={12} sm={5} lg={4}>
              <div className='geofences__card'>
                <TabContext value={activeRightTab}>
                  <div className='app-tabs'>
                    <TabList onChange={handleRightTabchange} aria-label='lab API tabs example'>
                      <Tab label='Airport geofences' value='airports' sx={{ textTransform: 'none' }} />
                      <Tab label='Facility geofences' value='facility' sx={{ textTransform: 'none' }} />
                    </TabList>
                  </div>
                  <TabPanel value='airports' className='geofences__panel'>
                    <AirportGeofences onSeleted={handleGeofenceSelected} selectedItem={selectedGeofence} />
                  </TabPanel>
                  <TabPanel value='facility' className='geofences__panel'>
                    <FacilityGeofences onSeleted={handleGeofenceSelected} selectedItem={selectedGeofence} />
                  </TabPanel>
                </TabContext>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
    </React.Fragment>
  );
}
